/**
 * Two experts, one model, two lattices.
 *
 * Free's idea: "Is there any way to make 2 experts have a discussion about a
 * topic the user enters? Like pair a gardener in a debate with a hermeticist
 * over topic XYZ given by the user, then on the same model, different lattices.
 * Their conversation streams past gated with an adjustable delay between each
 * response."
 *
 * This is the clearest demonstration the bay has of its own thesis. The model
 * never changes. Only the memory does, and the two voices disagree because they
 * remember different things. If a visitor takes one idea away from the page, it
 * should be that one.
 *
 * WHY THIS IS THIN. No packStore refactor was needed: `loadPack` already
 * returns an independent object rather than mutating a singleton, so holding
 * two packs is just holding two values. And `converse()` already takes the pack
 * as a parameter and resets the KV cache before every generation, so alternating
 * lattices between turns is safe by construction rather than by care.
 *
 * The one thing that needs saying explicitly is the brief. A persona preamble
 * alone produces two experts who politely answer the same question in parallel;
 * it does not produce a discussion. So each speaker gets a short synthetic
 * exchange at the head of its history telling it who it is talking to and to
 * push back where it genuinely differs.
 */

import type { MindClient } from "./llm/client";
import type { EmbedClient } from "./lattice/embedClient";
import type { LoadedPack } from "./lattice/packStore";
import { converse, DEFAULT_CONFIG, type ConverseConfig } from "./converse";
import { openAperture } from "./lattice/aperture";
import { memoryBanner } from "./pure/toolParse";
import { prompt as renderPrompt, modePrompt, type DebateMode } from "./pure/promptKit";
import {
  introPrompt,
  questionPrompt,
  openingContext,
  rebuttalThinkPrompt,
  quoteOther,
  hasYielded,
  stripPrefill,
  rebuttalInstruction,
} from "./pure/debateRounds";
import {
  buildContext,
  flattenForModel,
  trailingPrefill,
  type Beat,
} from "./pure/debateContext";
import type { BudgetedMsg } from "./pure/contextBudget";
import { stripWrappingQuotes, judgeEchoAgainstAll, type EchoVerdict } from "./pure/echoScore";
import { coverage, type Coverage } from "./pure/coverage";
// Named helpers rather than `speaker.llm ?? llm` inlined sixteen times: the
// failure mode of missing one is silent. They live in pure/ so the precedence
// rules can be tested without a browser.
import { clientFor, preambleFor } from "./pure/speakerVoice";
import { fitToBudget, type FitReport } from "./contextFit";
import { MAX_CONTEXT_TOKENS } from "./llm/catalog";
import { turnMessages } from "./pure/debateTranscript";
import { deliberatePrompt, cleanDeliberation, deliberationQuery } from "./pure/deliberate";
import {
  parseAgenda,
  mergeAgenda,
  agendaHealth,
  type ProposedQuestion,
  type AgendaHealth,
} from "./pure/debateAgenda";

export * from "./pure/debateAgenda";
export * from "./pure/debateRounds";
export { clientFor, preambleFor } from "./pure/speakerVoice";

export type AgendaResult = {
  questions: ProposedQuestion[];
  /** Per side, keyed by pack key: did it pass, and how strong is its memory here. */
  health: Record<string, AgendaHealth & { persona: string }>;
};

/**
 * The agenda round. Both sides propose questions; the visitor picks.
 *
 * Runs BEFORE any debating, and is the cheapest thing in the whole flow, which
 * is the point: it smoke-tests both models, probes whether each lattice has any
 * purchase on the topic, and produces the question list that makes the opening
 * statements independent of each other.
 *
 * Retrieval strength is captured from the prefetch rather than inferred from the
 * prose. A model can write a perfectly well-formed question about a subject its
 * memory knows nothing about, and that combination is exactly the failure the
 * probe exists to catch.
 */
export async function proposeAgenda(
  llm: MindClient,
  embedder: EmbedClient,
  a: Speaker,
  b: Speaker,
  topic: string,
  opts: { perSide?: number; max?: number; config?: ConverseConfig; mode?: DebateMode } = {},
): Promise<AgendaResult> {
  const perSide = opts.perSide ?? 3;
  const config = opts.config ?? DEFAULT_CONFIG;
  const mode = opts.mode ?? "debate";
  const sides: { key: string; persona: string; questions: string[] }[] = [];
  const health: AgendaResult["health"] = {};

  for (const [self, other] of [
    [a, b],
    [b, a],
  ] as const) {
    let topScore: number | null = null;
    const client = clientFor(self, llm);
    client.label(`${short(self.persona)}: agenda`, { speaker: self.key, kind: "agenda" });
    const res = await converse(
      client,
      embedder,
      self.pack,
      [],
      modePrompt(mode, "agenda", { other: other.persona, topic, n: String(perSide) }),
      // Search the topic. The prompt itself is instructions, and embedding those
      // would retrieve against the word "debate".
      //
      // `bannerMode: "deliberate"` because THE AGENDA IS NOT AN ANSWER EITHER.
      // Third instance of the same conflict, measured 2026-07-27: this pass asks
      // for "questions, one per line, nothing else" while the answer banner
      // sitting directly above it ends "Start with the answer", and the banner
      // was winning. The Spark Expert returned three declarative recommendations
      // ("The recommended ambient temperature range for stable operation..."),
      // the parser correctly refused all three as non-questions, and a lattice
      // retrieving at 0.658 was reported as proposing nothing.
      { ...withVoice(self, config), queryOverride: topic, maxToolCalls: 0, bannerMode: "deliberate" },
      (ev) => {
        if (ev.type === "prefetch" || ev.type === "toolResult") {
          for (const r of ev.result.results) {
            const sc = r.score ?? 0;
            if (topScore === null || sc > topScore) topScore = sc;
          }
        }
      },
    );
    client.resetCache();

    const parsed = parseAgenda(res.answer, perSide);
    sides.push({ key: self.key, persona: self.persona, questions: parsed.questions });
    health[self.key] = {
      ...agendaHealth(parsed.questions, topScore, parsed.dropped),
      persona: self.persona,
    };
  }

  return {
    questions: mergeAgenda(sides[0], sides[1], opts.max ?? perSide * 2),
    health,
  };
}

export { debateBrief, historyFor, turnMessages } from "./pure/debateTranscript";

/**
 * "The Mind's Gardener" -> "Gardener", for labelling generations in the panel.
 *
 * Every persona in the catalogue carries the same prefix, so showing it once
 * per row is nine wasted characters in a column that is already competing for
 * width with the stage name.
 */
const short = (persona: string) => persona.replace(/^The Mind's /, "");

export type Speaker = {
  /** Pack key, used to tell the two apart. */
  key: string;
  /** "The Mind's Gardener". */
  persona: string;
  pack: LoadedPack;
  /**
   * This speaker's own interpreter, when it is not the shared one.
   *
   * The bay's thesis is one interpreter and swappable memory, so a single
   * client is the DEFAULT and every function here still takes one. But the
   * thesis is a claim, and a claim you cannot vary is not measurable: with one
   * model, every difference between the two sides is attributable to the
   * lattice by construction rather than by evidence. Giving a Speaker its own
   * client makes the control arm runnable.
   *
   * Left undefined, everything behaves exactly as it did.
   */
  llm?: MindClient;
  /**
   * Override the persona text baked into the pack.
   *
   * Free, 2026-07-27: "I would like to edit the preamble and the persona, I
   * know they are in the lattice but I think being able to adjust those is
   * important as much as the prompts." He is right, and the prompt registry
   * cannot serve this: its active set is GLOBAL, so a value edited there would
   * apply to both speakers at once and collapse them into one voice. The
   * preamble is per speaker, so its override has to live on the speaker.
   *
   * The pack stays the source of truth when this is undefined.
   */
  preamble?: string;
};

/**
 * `config` carrying this speaker's voice.
 *
 * A helper rather than two extra fields written out at each `converse` call,
 * for the same reason `clientFor` exists: three sites today is a list, and the
 * one you forget speaks in the pack's voice while the panel shows the edited
 * one. Silent, and exactly the sort of divergence this bay measures.
 */
function withVoice(self: Speaker, config: ConverseConfig): ConverseConfig {
  return { ...config, preamble: preambleFor(self), persona: self.persona };
}

export type DebateTurn = {
  speakerKey: string;
  persona: string;
  text: string;
  /** The private note this speaker wrote before answering, when deliberating. */
  note?: string;
  rowsTouched: number[];
  ms: number;
};

export type DebateOpts = {
  topic: string;
  /** Turns EACH, so the transcript is twice this long. */
  rounds: number;
  /** Pause between speakers, so a reader can keep up. Free asked for this. */
  delayMs: number;
  config?: ConverseConfig;
  /** Cooperative stop. Checked before each turn and during the delay. */
  control?: { stopped: boolean };
  onTurnStart?: (speaker: Speaker, index: number) => void;
  onToken?: (text: string) => void;
  onTurnEnd?: (turn: DebateTurn) => void;
  /** Counts down the gap between speakers, so the pause looks deliberate. */
  onWait?: (msLeft: number) => void;
  /**
   * Think before speaking: a short private note, then retrieval driven by that
   * note, then the reply. Free's design, and the answer to the second speaker
   * paraphrasing the first.
   */
  deliberate?: boolean;
  /** Cap on the note. Long enough to be a real thought, short enough to be cheap. */
  thinkTokens?: number;
  onThinkStart?: (speaker: Speaker) => void;
  onThink?: (speaker: Speaker, note: string) => void;
};

export const DEBATE_DEFAULTS = { rounds: 3, delayMs: 1500, deliberate: true, thinkTokens: 140 };

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Sleep in slices so a stop request does not have to wait out the whole gap. */
async function pause(ms: number, control?: { stopped: boolean }, onWait?: (left: number) => void) {
  const step = 100;
  let left = ms;
  while (left > 0) {
    if (control?.stopped) return;
    onWait?.(left);
    await sleep(Math.min(step, left));
    left -= step;
  }
  onWait?.(0);
}

export async function runDebate(
  llm: MindClient,
  embedder: EmbedClient,
  a: Speaker,
  b: Speaker,
  opts: DebateOpts,
): Promise<DebateTurn[]> {
  const { topic, rounds, delayMs, control, config = DEFAULT_CONFIG } = opts;
  const turns: DebateTurn[] = [];
  const total = rounds * 2;

  for (let i = 0; i < total; i++) {
    if (control?.stopped) break;

    const self = i % 2 === 0 ? a : b;
    const other = i % 2 === 0 ? b : a;
    opts.onTurnStart?.(self, i);

    // History and the incoming turn are computed together, because converse
    // appends the user turn itself and the two must alternate ACROSS that seam.
    const { history, userTurn } = turnMessages(self, other, topic, turns);

    const t0 = performance.now();
    const client = clientFor(self, llm);

    // Think before speaking. Skipped for the opening turn, which has nothing to
    // think ABOUT yet: the topic is its own prompt.
    let note = "";
    const previous = turns[turns.length - 1];
    if (opts.deliberate && previous) {
      opts.onThinkStart?.(self);
      const raw = await client.generate(
        [
          ...history,
          { role: "user", content: deliberatePrompt(other.persona, topic, previous.text) },
        ],
        {
          maxNewTokens: opts.thinkTokens ?? DEBATE_DEFAULTS.thinkTokens,
          // D1 stays off. This is a generation we issue and keep, not the
          // model's own hidden reasoning mode.
          enableThinking: false,
        },
      );
      note = cleanDeliberation(raw.text);
      opts.onThink?.(self, note);
      // The note rewrote the conversation prefix, so the cache is stale.
      client.resetCache();
    }
    const res = await converse(
      client,
      embedder,
      self.pack,
      history,
      // The note rides along with the opponent's words rather than becoming its
      // own assistant message. Two reasons. It is literally where Free put it,
      // right after their output. And a separate assistant message would make
      // the SECOND speaker's first exchange open with an assistant turn, since
      // its history is empty at that point, which chat templates dislike.
      note ? `${userTurn}

<think>${note}</think>` : userTurn,
      // The search query is the speaker's OWN note about what it needs, which
      // beats both the bare topic and the opponent's words. Falls back to the
      // topic when there is no note.
      { ...withVoice(self, config), queryOverride: deliberationQuery(note, topic) },
      (ev) => {
        if (ev.type === "token") opts.onToken?.(ev.text);
      },
    );

    const turn: DebateTurn = {
      speakerKey: self.key,
      persona: self.persona,
      text: res.answer,
      note: note || undefined,
      rowsTouched: res.rowsTouched,
      ms: performance.now() - t0,
    };
    turns.push(turn);
    opts.onTurnEnd?.(turn);

    // No trailing pause after the final turn: the debate is over, and a gap
    // there just looks like the page has hung.
    if (i < total - 1) await pause(delayMs, control, opts.onWait);
  }

  return turns;
}


/* ==================================================================== *
 *  The staged debate: prepare, read the question, prepare again, answer.
 *
 *  Free's protocol. Three generations per speaker per question: a staging
 *  think once per debate, a per-question think, then the answer. The answer is
 *  a CONTINUATION of the per-question think, prefilled into the assistant turn,
 *  which is the part `converse()` cannot express and the part that makes the
 *  note bind.
 * ==================================================================== */

/** A think pass. Runs through converse so memory search really works inside it. */
async function think(
  llm: MindClient,
  embedder: EmbedClient,
  self: Speaker,
  prompt: string,
  query: string,
  config: ConverseConfig,
  label = "think",
): Promise<string> {
  const client = clientFor(self, llm);
  client.label(`${short(self.persona)}: ${label}`, { speaker: self.key, kind: "think" });
  const res = await converse(
    client,
    embedder,
    self.pack,
    [],
    prompt,
    // One tool call allowed: "search your memories as needed" should mean it.
    //
    // `bannerMode: "deliberate"` because this is a NOTE, not an answer. The
    // ordinary banner ends with "Start with the answer" directly above a prompt
    // that says "Do not answer yet", and the banner was winning: notes came back
    // as second answers to the question instead of as the thing this agent
    // brings of its own. The binding sentence is unchanged.
    { ...withVoice(self, config), queryOverride: query, maxToolCalls: 1, maxNewTokens: 200, bannerMode: "deliberate" },
  );
  client.resetCache();
  return cleanDeliberation(res.answer);
}

/**
 * The staging think, once per debate per speaker.
 *
 * `retrieve` is an A/B, not a preference. With topic-level retrieval on, both
 * agents produced the same florid waffle and read as one voice, because a broad
 * topic retrieves weakly from a narrow lattice and weak facts still crowd the
 * context. Free's hypothesis is that the FIRST think should come from the
 * weights alone, leaving retrieval to the per-question pass where the query is
 * narrow enough to hit. Measured by `runStagingAB` rather than assumed.
 */
export async function prepareSpeaker(
  llm: MindClient,
  embedder: EmbedClient,
  self: Speaker,
  other: Speaker,
  topic: string,
  goesFirst: boolean,
  config: ConverseConfig = DEFAULT_CONFIG,
  retrieve = true,
  mode: DebateMode = "debate",
): Promise<{ intro: string; stagingNote: string }> {
  const intro = introPrompt(other.persona, topic, goesFirst, undefined, mode);
  if (retrieve) {
    return { intro, stagingNote: await think(llm, embedder, self, intro, topic, config) };
  }
  // No pack, no prefetch, no tool loop: the stance comes from the weights and
  // the persona preamble, which is the whole point of the arm.
  const client = clientFor(self, llm);
  client.resetCache();
  client.label(`${short(self.persona)}: staging think`, { speaker: self.key, kind: "think" });
  const out = await client.generate(
    [
      {
        role: "system",
        // Through the registry, so an edit in the lab reaches the staging think
        // as well. The default is bare `{preamble}`, so this is identical to
        // what it was until someone changes it.
        content: renderPrompt("system", {
          preamble: preambleFor(self),
          persona: self.persona,
        }),
      },
      { role: "user", content: intro },
    ],
    { maxNewTokens: 200, enableThinking: false },
  );
  client.resetCache();
  return { intro, stagingNote: cleanDeliberation(out.text) };
}

export type Statement = {
  speakerKey: string;
  persona: string;
  question: string;
  questionNote: string;
  answer: string;
  rowsTouched: number[];
  ms: number;
};

/**
 * One agent's OPENING statement on one question.
 *
 * It sees the question and its own preparation. It does not see the opponent's
 * answer, which is what makes the openings independent and the echo impossible.
 */
export async function answerQuestion(
  llm: MindClient,
  embedder: EmbedClient,
  self: Speaker,
  staged: { intro: string; stagingNote: string },
  question: string,
  questionIndex: number,
  config: ConverseConfig = DEFAULT_CONFIG,
  limitOverride?: number,
  mode: DebateMode = "debate",
): Promise<Statement> {
  const t0 = performance.now();

  const questionNote = await think(
    llm,
    embedder,
    self,
    questionPrompt(question, questionIndex),
    question,
    config,
  );

  // Retrieve with the note-derived query, which is this agent's own
  // articulation of what it needs rather than the bare question.
  const ap = await openAperture(self.pack, embedder, deliberationQuery(questionNote, question), config.k);
  const facts = ap.results.length > 0 ? memoryBanner(ap.results) : undefined;

  // Budgeted too, though it rarely bites here: the opening context is short by
  // construction. The one part that is not bounded by the protocol is the
  // memory banner, and a guard that covers only the path you expected to
  // overflow is not a guard.
  const ctx = openingContext({
    mode,
    intro: staged.intro,
    stagingNote: staged.stagingNote,
    question,
    questionIndex,
    questionNote,
    facts,
  });
  const client = clientFor(self, llm);
  const fitted = await fitToBudget(client, ctx, contextLimit(config, limitOverride), {
    preamble: preambleFor(self),
  });

  client.resetCache();
  client.label(`${short(self.persona)}: OPENING`, { speaker: self.key, kind: "output" });
  const out = await client.generate(flattenForModel(fitted.msgs), {
    maxNewTokens: config.maxNewTokens,
    enableThinking: false,
    // The context ends on this speaker's own note, which is the whole point of
    // the staged protocol. Whether the template lets the model CONTINUE it is
    // the open question in #48.
    openPrefill: config.openPrefill,
  });

  return {
    speakerKey: self.key,
    persona: self.persona,
    question,
    questionNote,
    answer: stripPrefill(trailingPrefill(fitted.msgs), out.text),
    rowsTouched: ap.results.map((r, i) => (r as { row?: number }).row ?? i),
    ms: performance.now() - t0,
  };
}

/** Both agents answer one question independently. A goes first by convention. */
export async function runOpeningRound(
  llm: MindClient,
  embedder: EmbedClient,
  a: Speaker,
  b: Speaker,
  stagedA: { intro: string; stagingNote: string },
  stagedB: { intro: string; stagingNote: string },
  question: string,
  questionIndex: number,
  config: ConverseConfig = DEFAULT_CONFIG,
  onStatement?: (s: Statement) => void,
): Promise<Statement[]> {
  const out: Statement[] = [];
  for (const [self, staged] of [
    [a, stagedA],
    [b, stagedB],
  ] as const) {
    const s = await answerQuestion(llm, embedder, self, staged, question, questionIndex, config);
    out.push(s);
    onStatement?.(s);
  }
  return out;
}


/* ==================================================================== *
 *  The rebuttal round.
 *
 *  Free's call: agents see each other's ANSWER verbatim and never each other's
 *  think block. A note stays with the agent that thought it, and with the
 *  visitor watching.
 *
 *  Alternates until someone rests or the cap bites. The cap is not optional: a
 *  model that always chooses to respond would otherwise never terminate.
 * ==================================================================== */

export type Rebuttal = {
  speakerKey: string;
  persona: string;
  note: string;
  text: string;
  /** True when this agent said it had nothing further. */
  yielded: boolean;
  /** What the context budget spent to make room for this turn, if anything. */
  fit?: FitReport;
  /**
   * Rows this turn's own retrieval touched. Empty means the lattice had nothing
   * above the relevance floor, which is the condition under which a speaker has
   * only the opponent's paragraph to work from.
   */
  rowsTouched: number[];
  /** How much of this reply was the opponent's phrasing. Scored every turn. */
  echo: EchoVerdict;
  ms: number;
};

export const REBUTTAL_DEFAULTS = { maxExchanges: 4 };

/**
 * Room left for the context, once the reply itself is accounted for.
 *
 * `MAX_CONTEXT_TOKENS` is the whole window, and a context filling all of it
 * leaves nothing to generate into. Until now that constant was declared and
 * enforced nowhere at all, so a long debate would simply have run off the end.
 */
const GEN_HEADROOM = 256;

export function contextLimit(config: ConverseConfig, override?: number): number {
  return override ?? Math.max(512, MAX_CONTEXT_TOKENS - config.maxNewTokens - GEN_HEADROOM);
}

/** Everything said on one question, in the order it was said. */
type Said = { speakerKey: string; note?: string; text: string };

/**
 * One speaker's view of the shared transcript.
 *
 * A chat template has only "user" and "assistant", so each speaker sees its own
 * turns as assistant and the other's as user. Get that backwards and the model
 * argues with its own words, which reads as bad writing rather than as a bug.
 *
 * The opponent's turns are ATTRIBUTED rather than dropped in bare. Whose words
 * these are is the exact thing the rebuttal round keeps losing track of.
 */
function beatsFor(selfKey: string, otherPersona: string, said: Said[]): Beat[] {
  return said.map<Beat>((s) =>
    s.speakerKey === selfKey
      ? { who: "self", note: s.note, text: s.text }
      : { who: "other", text: quoteOther(otherPersona, s.text) },
  );
}

function lastSaidBy(said: Said[], key: string): string | undefined {
  for (let i = said.length - 1; i >= 0; i--) if (said[i].speakerKey === key) return said[i].text;
  return undefined;
}

export async function runRebuttalRound(
  llm: MindClient,
  embedder: EmbedClient,
  a: Speaker,
  b: Speaker,
  stagedFor: Record<string, { intro: string; stagingNote: string }>,
  question: string,
  openings: Statement[],
  opts: {
    maxExchanges?: number;
    config?: ConverseConfig;
    control?: { stopped: boolean };
    onRebuttal?: (r: Rebuttal) => void;
    mode?: DebateMode;
    /**
     * Force a smaller window than the model's, to exercise compression. The
     * only way to see the budget work without staging an hour-long debate.
     */
    contextLimit?: number;
    onFit?: (speaker: Speaker, report: FitReport) => void;
    /**
     * The exact labelled context each turn was generated from, after budgeting.
     * Exists so a harness can check that nothing spoken was paraphrased, which
     * is the one property the whole compression design turns on and the one
     * that cannot be checked from the transcript alone.
     */
    onContext?: (speaker: Speaker, msgs: BudgetedMsg[]) => void;
  } = {},
): Promise<Rebuttal[]> {
  const config = opts.config ?? DEFAULT_CONFIG;
  const cap = opts.maxExchanges ?? REBUTTAL_DEFAULTS.maxExchanges;
  const limit = contextLimit(config, opts.contextLimit);
  const mode = opts.mode ?? "debate";
  const out: Rebuttal[] = [];

  // The whole exchange, in order, shared between both speakers and turned into
  // each one's private view at the moment it speaks. Openings first, A then B,
  // because the intro told them who goes first and the transcript should agree.
  const said: Said[] = openings.map((s) => ({
    speakerKey: s.speakerKey,
    note: s.questionNote,
    text: s.answer,
  }));
  const done = new Set<string>();

  for (let i = 0; i < cap; i++) {
    if (opts.control?.stopped) break;
    const self = i % 2 === 0 ? a : b;
    const other = i % 2 === 0 ? b : a;
    if (done.has(self.key)) continue;
    // Nothing to answer yet means nothing to rebut.
    const theirs = lastSaidBy(said, other.key);
    if (!theirs) continue;

    const t0 = performance.now();
    const staged = stagedFor[self.key];

    const note = await think(
      llm,
      embedder,
      self,
      rebuttalThinkPrompt(other.persona, question, theirs, undefined, mode),
      // Query on the question, not on their words: retrieving with the
      // opponent's text is what produced the paraphrasing.
      question,
      config,
    );

    const ap = await openAperture(
      self.pack,
      embedder,
      deliberationQuery(note, question),
      config.k,
    );
    const facts = ap.results.length > 0 ? memoryBanner(ap.results) : undefined;

    // The context ACCUMULATES. It used to be a fixed six-message window that
    // showed each side only the other's most recent paragraph, so an agent
    // could not see the arc of its own argument and had nothing to notice it
    // was repeating. It also meant the context never grew, which is why the
    // budget below had nothing to do until now.
    const ctx = buildContext({
      intro: staged.intro,
      stagingNote: staged.stagingNote,
      question,
      beats: beatsFor(self.key, other.persona, said),
      instruction: rebuttalInstruction(undefined, mode),
      facts,
      liveNote: note,
    });

    const client = clientFor(self, llm);
    const fitted = await fitToBudget(client, ctx, limit, {
      preamble: preambleFor(self),
    });
    if (fitted.report.folded || fitted.report.dropped) opts.onFit?.(self, fitted.report);
    opts.onContext?.(self, fitted.msgs);

    // Read the prefill back off the fitted context rather than rebuilding it.
    // Under heavy pressure the live note is foldable like any other, and a
    // hand-written prefill would then strip text the model never saw.
    const prefill = trailingPrefill(fitted.msgs);

    client.resetCache();
    client.label(`${short(self.persona)}: rebuttal ${i + 1}`, { speaker: self.key, kind: "output" });
    const gen = await client.generate(flattenForModel(fitted.msgs), {
      maxNewTokens: config.maxNewTokens,
      enableThinking: false,
      openPrefill: config.openPrefill,
    });

    // Strip the decorative wrapper before STORING, not just before display.
    // A reply the model wrapped in quotes gets quoted again on the way back in,
    // and after three exchanges the boundary between their words and mine has
    // no marker left that a 1B model can trust.
    const text = stripWrappingQuotes(stripPrefill(prefill, gen.text));
    const yielded = hasYielded(text);
    if (yielded) done.add(self.key);
    else said.push({ speakerKey: self.key, note, text });

    const r: Rebuttal = {
      speakerKey: self.key,
      persona: self.persona,
      note,
      text,
      yielded,
      fit: fitted.report,
      rowsTouched: ap.results.map((x, ix) => (x as { row?: number }).row ?? ix),
      // Scored against EVERY earlier thing the opponent said, not just the turn
      // being answered. Scoring against the last turn alone called a transcript
      // clean that contained a fifteen-word clause lifted from two turns back.
      echo: judgeEchoAgainstAll(
        text,
        said.filter((s) => s.speakerKey === other.key).map((s) => s.text),
      ),
      ms: performance.now() - t0,
    };
    out.push(r);
    opts.onRebuttal?.(r);

    if (done.size === 2) break;
  }
  return out;
}

export type QuestionRound = {
  question: string;
  openings: Statement[];
  rebuttals: Rebuttal[];
  /**
   * Did each turn bring new lattice rows, or only new words?
   *
   * The success signal for COLLABORATION, where echo cannot serve: agreement is
   * the goal there, so two speakers converging might be the point or might be
   * one voice twice. The union of retrieved rows can only grow if the second
   * lattice actually had something the first did not.
   */
  coverage: Coverage;
};

/**
 * A whole debate: agenda already chosen, then each question gets its own
 * opening pair followed by rebuttals. Per-question, as Free specified.
 */
export async function runStagedDebate(
  llm: MindClient,
  embedder: EmbedClient,
  a: Speaker,
  b: Speaker,
  topic: string,
  questions: string[],
  opts: {
    config?: ConverseConfig;
    maxExchanges?: number;
    stagingRetrieval?: boolean;
    mode?: DebateMode;
    control?: { stopped: boolean };
    /**
     * A new question is starting.
     *
     * Exists for the visitor page, which builds its transcript from the
     * generation tap and so knows who spoke but not what they were answering.
     * Fired at the top of the loop rather than derived from `onStatement`,
     * which arrives after the turn it introduces and would put a heading below
     * its own answers.
     */
    onQuestion?: (question: string, index: number) => void;
    onStatement?: (s: Statement) => void;
    onRebuttal?: (r: Rebuttal) => void;
    /** Force a smaller window than the model's, to exercise compression. */
    contextLimit?: number;
    onFit?: (speaker: Speaker, report: FitReport) => void;
    onContext?: (speaker: Speaker, msgs: BudgetedMsg[]) => void;
  } = {},
): Promise<QuestionRound[]> {
  const config = opts.config ?? DEFAULT_CONFIG;
  const retrieve = opts.stagingRetrieval ?? false; // measured: stance, not facts
  const mode = opts.mode ?? "debate";
  const stagedA = await prepareSpeaker(llm, embedder, a, b, topic, true, config, retrieve, mode);
  const stagedB = await prepareSpeaker(llm, embedder, b, a, topic, false, config, retrieve, mode);
  const stagedFor = { [a.key]: stagedA, [b.key]: stagedB };

  const rounds: QuestionRound[] = [];
  for (let qi = 0; qi < questions.length; qi++) {
    if (opts.control?.stopped) break;
    const question = questions[qi];
    opts.onQuestion?.(question, qi);
    const openings = await runOpeningRound(
      llm, embedder, a, b, stagedA, stagedB, question, qi, config, opts.onStatement,
    );
    const rebuttals = await runRebuttalRound(llm, embedder, a, b, stagedFor, question, openings, {
      mode,
      maxExchanges: opts.maxExchanges,
      config,
      control: opts.control,
      onRebuttal: opts.onRebuttal,
      contextLimit: opts.contextLimit,
      onFit: opts.onFit,
      onContext: opts.onContext,
    });
    rounds.push({
      question,
      openings,
      rebuttals,
      // Openings first, then rebuttals, which is the order they happened in.
      coverage: coverage([
        ...openings.map((o) => ({ speakerKey: o.speakerKey, rowsTouched: o.rowsTouched })),
        ...rebuttals.map((r) => ({ speakerKey: r.speakerKey, rowsTouched: r.rowsTouched })),
      ]),
    });
  }
  return rounds;
}
